import * as React from 'react';

// @ts-expect-error - imported from .js/.jsx file
import {PencilToPaperIcon} from 'shells/icon/svg/pencil-to-paper-icon';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';
import {AddButtonRow} from 'shells/table/table-body/add-button-row';

import type {
    GetCustomFieldStagesets_stagesets as Stageset,
    GetCustomFieldStagesets_stagesets_customFields_shown_customField as CustomField,
} from 'nutshell-graphql-types';

import {
    CustomFieldsPipelineDraggableList,
    TOP_THREE_BUCKET_ID,
    TOP_THREE_BUCKET_AMOUNT,
    SIDEBAR_BUCKET_ID,
} from './custom-fields-pipeline-draggable-list';
import {CustomFieldRowItem} from './custom-fields-row-item';
import {useUpdateCustomFieldPositions} from './graphql/hooks';
import type {ListItemStat} from './types';

type ListItem = {
    id: string;
    component: React.ReactNode;
    bucketId: string;
    isActive?: boolean;
};

type Props = {
    onOpenCreateModal: () => void;
    pipeline: Stageset;
    onOpenEditModal: (fieldId: string) => void;
    onArchive: (fieldId: string) => void;
};

const POSITION_OFFSET = 1000;

export function CustomFieldsPipelineTable(props: Props) {
    const [items, setItems] = React.useState<Array<ListItem>>([]);
    const itemsRef = React.useRef<Array<ListItem>>([]);

    const {updateCustomFieldPositions} = useUpdateCustomFieldPositions();

    // Converts our index to a position for the custom field that the backend
    // expects.
    function getCustomFieldPosition(bucketId: string, index: number) {
        const numItemsInBucketZero = itemsRef.current.filter(
            (field) => field.bucketId === '0'
        ).length;

        if (bucketId === '0') {
            return index + POSITION_OFFSET;
        } else if (bucketId === '1') {
            return index + 3 + POSITION_OFFSET - numItemsInBucketZero;
        } else if (bucketId === '2') {
            // If we are in the hidden bucket, return a null position to signify we
            // are in a positionless state that should be handled to remove the
            // mapping if still active
            return null;
        }

        return null;
    }

    const onReorderFromActionMenu = React.useCallback(
        (fieldId: string) => () => {
            // Find current item and its bucket
            const currentItem = itemsRef.current.find((item) => item.id === fieldId);
            if (!currentItem) {
                return;
            }

            // Get items in current bucket and find index of current item within that bucket
            const itemsInBucket = itemsRef.current.filter(
                (item) => item.bucketId === currentItem.bucketId
            );
            const bucketIndex = itemsInBucket.findIndex((item) => item.id === fieldId);

            // Move to opposite bucket (hidden bucket to sidebar bucket, or sidebar bucket/top three bucket to hidden)
            const destinationBucket = currentItem.bucketId === '2' ? '1' : '2';
            handleReorder(
                bucketIndex,
                0,
                currentItem.bucketId,
                destinationBucket,
                props.pipeline.id
            );
        },
        // Avoiding circular dependency by not including handleReorder in the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [itemsRef, props.pipeline.id]
    );

    const isFieldShownInPipeline = React.useCallback((fieldId: string) => {
        const item = itemsRef.current.find((currItem) => currItem.id === fieldId);

        return item ? item.bucketId !== '2' : false;
    }, []);

    const buildStats = React.useCallback((field: CustomField): Array<ListItemStat> => {
        const stats = field.customFieldStats;
        const numOfForms = stats && stats.numOfForms ? stats.numOfForms : 0;

        const statsList: Array<ListItemStat> = [
            {
                id: `forms-${field.attribute.displayName}`,
                count: numOfForms,
                icon: PencilToPaperIcon,
                tooltipText: `${numOfForms} form${numOfForms === 1 ? '' : 's'} using this field`,
            },
        ];

        return statsList;
    }, []);

    React.useEffect(() => {
        let newItems = [];
        const shownItems = props.pipeline.customFields.shown.map((field) => {
            const bucketId = field.position <= 1002 && field.position >= 1000 ? '0' : '1';
            const customField = field.customField;

            const statsList = buildStats(customField);

            return {
                id: customField.id,
                component: (
                    <CustomFieldRowItem
                        onArchive={() => props.onArchive(customField.id)}
                        displayName={customField.attribute.displayName}
                        fieldType={customField.attribute.displayType}
                        description={customField.attribute.description}
                        onEdit={() => props.onOpenEditModal(customField.id)}
                        isViewingArchived={false}
                        isOnPipelineTable={true}
                        fieldIsShownInPipeline={() => isFieldShownInPipeline(customField.id)}
                        onReorderField={onReorderFromActionMenu(customField.id)}
                        stats={statsList}
                    />
                ),
                bucketId,
            };
        });

        const hiddenItems = props.pipeline.customFields.hidden.map((field) => {
            const statsList = buildStats(field);

            return {
                id: field.id,
                component: (
                    <CustomFieldRowItem
                        onArchive={() => props.onArchive(field.id)}
                        displayName={field.attribute.displayName}
                        fieldType={field.attribute.displayType}
                        description={field.attribute.description}
                        onEdit={() => props.onOpenEditModal(field.id)}
                        onRestore={() => {}}
                        isViewingArchived={false}
                        isOnPipelineTable={true}
                        fieldIsShownInPipeline={() => isFieldShownInPipeline(field.id)}
                        onReorderField={onReorderFromActionMenu(field.id)}
                        stats={statsList}
                    />
                ),
                bucketId: '2',
            };
        });

        newItems = [...shownItems, ...hiddenItems];
        setItems(newItems);
        itemsRef.current = newItems;
    }, [props, onReorderFromActionMenu, isFieldShownInPipeline, buildStats]);

    const handleReorder = (
        startIndex: number,
        endIndex: number,
        sourceBucketId: string,
        destinationBucketId: string,
        stagesetId: string
    ) => {
        let newItems = [...itemsRef.current];

        const sourceItems = newItems.filter((item) => item.bucketId === sourceBucketId);
        const [reorderedItem] = sourceItems.splice(startIndex, 1);

        // @ts-expect-error
        reorderedItem.bucketId = destinationBucketId;

        let destinationItems;
        if (destinationBucketId === sourceBucketId) {
            destinationItems = sourceItems;
        } else {
            destinationItems = newItems.filter(
                // @ts-expect-error
                (item) => item.bucketId === destinationBucketId && item.id !== reorderedItem.id
            );
        }
        // @ts-expect-error
        destinationItems.splice(endIndex, 0, reorderedItem);

        let shouldAddToSidebar = false;
        let itemToRemove;
        // Remove last item from top three bucket if bucket is full when adding another
        if (
            destinationBucketId === TOP_THREE_BUCKET_ID &&
            destinationItems.length > TOP_THREE_BUCKET_AMOUNT
        ) {
            // If item is moved to last position of bucket, we need to remove the 'old' last item, as opposed to the newly added item
            if (endIndex === TOP_THREE_BUCKET_AMOUNT) {
                [itemToRemove] = destinationItems.splice(TOP_THREE_BUCKET_AMOUNT - 1, 1);
            } else {
                itemToRemove = destinationItems.pop();
            }
            // @ts-expect-error
            itemToRemove.bucketId = SIDEBAR_BUCKET_ID;
            // Make sure that the removed item is added to the top of the sidebar bucket
            if (sourceBucketId === SIDEBAR_BUCKET_ID) {
                // @ts-expect-error
                sourceItems.unshift(itemToRemove);
            } else {
                shouldAddToSidebar = true;
            }
        }

        // Get all items that are not in the source or destination buckets
        const otherBucketItems = itemsRef.current.filter(
            (item) => item.bucketId !== sourceBucketId && item.bucketId !== destinationBucketId
        );

        // If we moved from hidden bucket to full top three bucket, we need to remove the item from the sidebar bucket and re add it so it shows at top of sidebar bucket
        if (shouldAddToSidebar && itemToRemove) {
            otherBucketItems.splice(otherBucketItems.indexOf(itemToRemove), 1);
            otherBucketItems.unshift(itemToRemove);
        }

        // Recombine the source and destination items if they are in different
        // buckets, otherwise the buckets are the same and we just need to pick
        // one (sourceItems in this case).
        if (destinationBucketId !== sourceBucketId) {
            newItems = [...sourceItems, ...destinationItems, ...otherBucketItems];
        } else {
            newItems = [...otherBucketItems, ...sourceItems];
        }

        // Seperate the items into their respective buckets so we can order the positions correctly
        const bucketZeroItems = newItems.filter((item) => item.bucketId === '0');
        const bucketOneItems = newItems.filter((item) => item.bucketId === '1');
        const bucketTwoItems = newItems.filter((item) => item.bucketId === '2');

        // Deconstruct bucket items in order so their positions are correct
        newItems = [...bucketZeroItems, ...bucketOneItems, ...bucketTwoItems];

        setItems(newItems);
        itemsRef.current = newItems;

        const fieldPositions = newItems.map((item, index) => {
            return {
                fieldId: item.id,
                position: getCustomFieldPosition(item.bucketId, index),
            };
        });

        updateCustomFieldPositions({
            fieldPositions: fieldPositions,
            entityType: 'LEAD',
            stagesetId: stagesetId,
        });
    };

    return (
        <div className='full-width'>
            <div className='flex flex-dir-col gap-16'>
                <LegacySetupTableWrapper title={`${props.pipeline.name} custom fields`}>
                    <AddButtonRow
                        buttonText='Create a new field'
                        onClick={props.onOpenCreateModal}
                    />
                    <div className='flex flex-dir-col gap-16 flex-grow'>
                        <CustomFieldsPipelineDraggableList
                            listItems={items}
                            handleReorder={handleReorder}
                            showDraggableRowOnlyOnHover={false}
                            stagesetId={props.pipeline.id}
                        />
                    </div>
                </LegacySetupTableWrapper>
            </div>
        </div>
    );
}
